import { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiError } from './ApiError';
import { ActionResult } from './types';

function isApiError<T>(error: unknown): error is ApiError<T> {
  return error instanceof ApiError;
}

export function handleActionErrors<T>(error: unknown): ActionResult<T> {
  if (isApiError<T>(error)) {
    return {
      errors: error.errors,
      errorMessage: error.message,
      ok: false,
    };
  }

  return {
    errorMessage: 'An unexpected error occurred',
    ok: false,
  };
}

/**
 * Consumer-side counterpart to {@link handleActionErrors}: extracts the
 * user-displayable messages from an `ActionResult` (read via `useActionData`).
 */
export function getActionErrorMessages<T>(actionData: ActionResult<T>): string[] {
  const fieldMessages = Object.values(actionData?.errors ?? {}).flat() as string[];

  if (fieldMessages.length) {
    return fieldMessages;
  }

  return actionData?.errorMessage ? [actionData.errorMessage] : [];
}

export function handleApiError(error: unknown, fallbackMessage?: string): string {
  if (isApiError(error)) {
    return error.message;
  }

  return fallbackMessage || 'An unexpected error occurred';
}

export function handleApiErrorMessages<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>
): string {
  if (isApiError<T>(error)) {
    if (error.errors) {
      Object.entries(error.errors).forEach(([field, messages]) =>
        setError(field as Path<T>, {
          type: 'api',
          message: messages.join(' '),
        })
      );
    }

    return error.message;
  }

  return "Sorry, that didn't work. Please try resubmitting the form";
}

export function handleApiErrorsByKey<T>(
  error: unknown,
  key: keyof T | string,
  options?: { startsWith?: boolean }
): string[] | undefined {
  if (isApiError<T>(error) && error.errors) {
    if (options?.startsWith && typeof key === 'string') {
      const matchedErrors: string[] = [];

      // Collect errors from all keys that start with the given string
      for (const errorKey in error.errors) {
        if (errorKey.startsWith(key)) {
          matchedErrors.push(...error.errors[errorKey as keyof T]);
        }
      }

      return matchedErrors.length > 0 ? matchedErrors : undefined;
    }

    if (key in error.errors) {
      return error.errors[key as keyof T];
    }
  }
}
